import produce from "immer";
import { CustomizationState } from "../";
import { Configuration } from "../../../types/customization/configuration";
import { SelectionPhoto } from "../../../types/selection/selection";
import { countPages } from "../../../utils/customization/configuration";
import { countMinMaxTotalEmptyEditablePicturesInContent } from "../../../utils/customization/content";
import { getCoverFilteredPages, getNoCoverDefinitions } from "../../../utils/customization/cover";
import {
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
  findValidPageDefinitionsToAdd,
} from "../../../utils/customization/finders";
import { countEmptyEditablePictures } from "../../../utils/customization/page";
import { changeOnePagePageDefinition } from "../../../utils/customization/pageDefinition";
import {
  countEditablePicturesInPage,
  findMinMaxEditablePictures,
  getNextLayoutWithMorePictures,
  layoutsByEditablePicturesCount,
} from "../../../utils/customization/template";
import { TemplatePageDefinitionsByCountKey } from "../../../utils/customization/template";
import { doAddPage } from "./addPage";
import { doAddPicture } from "./addPicture";

export interface AddPicturesWithAutofillAction {
  configuration: Configuration | undefined;
  selectedPictures: SelectionPhoto[];
  type: "addPicturesWithAutofill";
}
interface LayoutsByCountKey {
  [key: string]: TemplatePageDefinitionsByCountKey | null;
}

export function addPicturesWithAutofill(
  state: CustomizationState,
  action: AddPicturesWithAutofillAction
): CustomizationState {
  return produce(state, (draftState) => {
    const { configuration, selectedPictures } = action;
    const { content, template } = draftState;
    const isPrint = template.templateType === "print";

    const minPages = configuration && configuration.pages && configuration.pages.min;
    const maxPages = configuration && configuration.pages && configuration.pages.max;
    const increment =
      (!isPrint && configuration && configuration.pages && configuration.pages.increment) || 1;

    const conditionFilteredPageDefinitions = findPageDefinitionsWithValidCondition(
      template,
      content
    );
    const noCoverPageDefinitions = conditionFilteredPageDefinitions
      ? getNoCoverDefinitions(conditionFilteredPageDefinitions)
      : [];
    const pageDefinitionsToAdd = findValidPageDefinitionsToAdd(template, content);
    const pageDefinitionsToAddKeys = pageDefinitionsToAdd.map(({ key }) => key);
    let pagesWithoutCover = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
    // eventually check editableTexts ?
    let pagesWithoutEditablePictures = pagesWithoutCover.filter(
      (contentPage) => contentPage.editablePictures.length === 0
    );

    let pagesWithEditablePictures = pagesWithoutCover.filter(
      (contentPage) => contentPage.editablePictures.length > 0
    );

    let nbCurrentPages = countPages(content);

    const pagesCount = countPages(content, noCoverPageDefinitions);
    const totalMinMaxSlotsInContent = countMinMaxTotalEmptyEditablePicturesInContent(
      template,
      pagesWithoutCover
    );
    const minMax = findMinMaxEditablePictures(noCoverPageDefinitions);
    const minMaxToAdd = findMinMaxEditablePictures(pageDefinitionsToAdd);
    const nbMinPages = minPages ? minPages : 1;
    const nbMaxPages = maxPages ? maxPages : 0;

    const minEmptyEditablePictures =
      Math.max(0, minMaxToAdd.min * (nbMinPages - pagesCount)) + totalMinMaxSlotsInContent.min;
    const maxEmptyEditablePictures =
      nbMaxPages > 0
        ? minMaxToAdd.max * (nbMaxPages - pagesCount) + totalMinMaxSlotsInContent.max
        : 0;
    const maxEmptyEditablePicturesWithMinPages =
      Math.max(0, minMax.max * (nbMinPages - pagesCount)) + totalMinMaxSlotsInContent.max;

    const lastPageDefinitionKey =
      pagesWithoutCover.length > 0
        ? pagesWithoutCover[pagesWithoutCover.length - 1].pageDefinitionKey
        : noCoverPageDefinitions[0].key;
    const lastPageDefinitionKeyToAdd = pageDefinitionsToAddKeys.includes(lastPageDefinitionKey)
      ? lastPageDefinitionKey
      : pageDefinitionsToAddKeys[0];
    const layoutsByPageDefinitionKey: LayoutsByCountKey = {};
    for (const noCoverPageDefinition of noCoverPageDefinitions) {
      const layouts = layoutsByEditablePicturesCount(
        lastPageDefinitionKey,
        noCoverPageDefinitions,
        false
      );
      layoutsByPageDefinitionKey[noCoverPageDefinition.key] = layouts;
    }

    if (maxPages === 1) {
      // > if empty and too much pictures, use bigger layout
      // > if not empty, fill empty slots
      const uniqPage = pagesWithoutCover[0];
      if (!uniqPage) {
        return;
      }
      let nbEmptySlots = countEmptyEditablePictures([uniqPage], template);

      if (uniqPage.editablePictures.length === 0) {
        const pageDefinition = findPageDefinition(uniqPage.pageDefinitionKey, template);
        const nbEditablePicturesInPage = countEditablePicturesInPage(pageDefinition);
        if (selectedPictures.length > nbEditablePicturesInPage) {
          const newPageDefinition = getNextLayoutWithMorePictures(
            uniqPage.pageDefinitionKey,
            noCoverPageDefinitions,
            selectedPictures.length - nbEmptySlots
          );
          if (newPageDefinition) {
            changeOnePagePageDefinition(uniqPage, draftState, newPageDefinition.key);
            nbEmptySlots = countEditablePicturesInPage(newPageDefinition);
          }
        }
      }
      for (let i = 0; i < selectedPictures.length && i < nbEmptySlots; i++) {
        const selectionPhoto = selectedPictures[i];
        doAddPicture(draftState, {
          configuration,
          pageKey: uniqPage.key,
          selectionPhoto,
          tryToFillFirstEmptyPicture: true,
          type: "addPicture",
        });
      }

      return;
    }

    const layoutsByEditablePictureCountToAdd =
      layoutsByPageDefinitionKey[lastPageDefinitionKeyToAdd];
    const layoutsKeysToAdd = getKeysByOrderAsc(layoutsByEditablePictureCountToAdd);
    const biggestLayoutLengthToAdd = layoutsKeysToAdd
      ? +layoutsKeysToAdd[layoutsKeysToAdd.length - 1]
      : null;

    if (
      isPrint &&
      nbCurrentPages < nbMinPages &&
      selectedPictures.length >= minEmptyEditablePictures
    ) {
      const minPagesToAdd = nbMinPages - nbCurrentPages;
      for (let i = 0; i < minPagesToAdd; i++) {
        doAddPage(draftState, {
          configuration,
          pageDefinitionKey: lastPageDefinitionKeyToAdd,
          type: "addPage",
        });
      }
      pagesWithoutCover = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
      pagesWithoutEditablePictures = pagesWithoutCover.filter(
        (contentPage) => contentPage.editablePictures.length === 0
      );
      nbCurrentPages += minPagesToAdd;
    }

    if (selectedPictures.length <= minEmptyEditablePictures) {
      // > add pictures, use smaller layout
      for (const contentPage of pagesWithoutEditablePictures) {
        // Should use layouts of same filter type
        const currentLayoutsByEditablePictureCount =
          layoutsByPageDefinitionKey[contentPage.pageDefinitionKey];
        const currentLayoutsKeys = getKeysByOrderAsc(currentLayoutsByEditablePictureCount);
        if (currentLayoutsByEditablePictureCount && currentLayoutsKeys) {
          const smallerLayoutKey =
            +currentLayoutsKeys[0] > 0 ? +currentLayoutsKeys[0] : +currentLayoutsKeys[1];
          changeOnePagePageDefinition(
            contentPage,
            draftState,
            currentLayoutsByEditablePictureCount[smallerLayoutKey][0].key
          );
        }
      }
    } else if (maxPages && selectedPictures.length >= maxEmptyEditablePictures) {
      // fill empty pictures
      // > add max pages with addNext filterType, use biggest layout
      const nbPackToAdd = (maxPages - pagesWithoutCover.length) / increment;
      if (!biggestLayoutLengthToAdd || !layoutsByEditablePictureCountToAdd) {
        return;
      }
      const biggestLayout = layoutsByEditablePictureCountToAdd[biggestLayoutLengthToAdd][0];
      const pageDefinitionKey = biggestLayout.key;
      for (let i = 0; i < nbPackToAdd; i++) {
        doAddPage(draftState, {
          configuration,
          pageDefinitionKey,
          type: "addPage",
        });
      }
      pagesWithoutCover = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
      pagesWithoutEditablePictures = pagesWithoutCover.filter(
        (contentPage) => contentPage.editablePictures.length === 0
      );
      for (const contentPage of pagesWithoutEditablePictures) {
        // Should use layouts of same filter types
        const currentLayoutsByEditablePictureCount =
          layoutsByPageDefinitionKey[contentPage.pageDefinitionKey];
        const currentLayoutsKeys = getKeysByOrderAsc(currentLayoutsByEditablePictureCount);
        const currentBiggestLayoutLength = currentLayoutsKeys
          ? +currentLayoutsKeys[currentLayoutsKeys.length - 1]
          : null;
        const currentBiggestLayout =
          currentLayoutsByEditablePictureCount && currentBiggestLayoutLength
            ? currentLayoutsByEditablePictureCount[currentBiggestLayoutLength][0]
            : biggestLayout;
        changeOnePagePageDefinition(contentPage, draftState, currentBiggestLayout.key);
      }
    } else {
      // fill empty spaces in edited pages then adapt layouts of remaining pages + add pages if needed
      if (maxPages && selectedPictures.length > maxEmptyEditablePicturesWithMinPages) {
        if (biggestLayoutLengthToAdd) {
          const nbPackToAdd =
            (selectedPictures.length - maxEmptyEditablePicturesWithMinPages) /
            (increment * biggestLayoutLengthToAdd);
          for (let i = 0; i < nbPackToAdd; i++) {
            doAddPage(draftState, {
              configuration,
              pageDefinitionKey: lastPageDefinitionKeyToAdd,
              type: "addPage",
            });
          }
          pagesWithoutCover = getCoverFilteredPages(content.pages, noCoverPageDefinitions);
          pagesWithoutEditablePictures = pagesWithoutCover.filter(
            (contentPage) => contentPage.editablePictures.length === 0
          );
          pagesWithEditablePictures = pagesWithoutCover.filter(
            (contentPage) => contentPage.editablePictures.length > 0
          );
        }
      }
      const nbPicturesLeft = selectedPictures.length;

      const nbPicturesToComplete = countEmptyEditablePictures(pagesWithEditablePictures, template);
      let nbPicturesLeftToInsert = nbPicturesLeft - nbPicturesToComplete;
      let nbPagesToFill = pagesWithoutCover.length - pagesWithEditablePictures.length;

      const pickNextLayoutIndex = (
        layoutCounts: string[],
        picturesToInsert: number,
        pagesToFill: number
      ): string => {
        const coefLayout = Math.ceil(picturesToInsert / pagesToFill);
        return (
          layoutCounts.find((key) => +key >= coefLayout) || layoutCounts[layoutCounts.length - 1]
        );
      };

      let layoutIndex: string | null = null;

      for (const contentPage of pagesWithoutEditablePictures) {
        // Should use layouts of same filter types
        const currentLayoutsByEditablePictureCount =
          layoutsByPageDefinitionKey[contentPage.pageDefinitionKey];
        const currentLayoutsKeys = getKeysByOrderAsc(currentLayoutsByEditablePictureCount);

        if (currentLayoutsByEditablePictureCount && currentLayoutsKeys) {
          const nextLayout = pickNextLayoutIndex(
            currentLayoutsKeys,
            nbPicturesLeftToInsert,
            nbPagesToFill
          );
          if (
            layoutIndex === null ||
            (nextLayout && nbPicturesLeftToInsert / +nextLayout <= nbPagesToFill)
          ) {
            layoutIndex = nextLayout;
          }
          changeOnePagePageDefinition(
            contentPage,
            draftState,
            currentLayoutsByEditablePictureCount[+layoutIndex][0].key
          );
          nbPicturesLeftToInsert = nbPicturesLeftToInsert - +layoutIndex;
          nbPagesToFill--;
        }
      }
    }

    for (const selectionPhoto of selectedPictures) {
      doAddPicture(draftState, {
        avoidCover: true,
        configuration,
        selectionPhoto,
        tryToFillFirstEmptyPicture: true,
        type: "addPicture",
      });
    }
  });
}

function getKeysByOrderAsc(
  layoutsByCount: TemplatePageDefinitionsByCountKey | null
): string[] | null {
  return layoutsByCount ? Object.keys(layoutsByCount).sort((a, b) => +a - +b) : null;
}
